import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { parcelAPI } from '../services/api';
import { WHATSAPP_NUMBER } from '../config/whatsapp';

const STATUS_LABELS = {
  draft: 'مسودة', payment_pending: 'بانتظار الدفع', payment_submitted: 'قيد المراجعة',
  paid: 'تم الدفع', waiting_delivery: 'بانتظار التسليم للمكتب', received_by_office: 'مستلم بالمكتب',
  waiting_transportation: 'بانتظار رحلة', loaded: 'محمّل على الباص', in_transit: 'في الطريق',
  arrived: 'وصل الوجهة', ready_for_collection: 'جاهز للاستلام', completed: 'مكتمل',
  returned: 'مرتجع', cancelled: 'ملغي', rejected: 'مرفوض',
  damaged: 'تعرض لتلف', lost: 'مفقود',
  dropoff_deadline_expired: 'انتهى موعد التسليم',
};

const EVENT_LABELS = {
  created: 'تم إنشاء الطلب', payment_submitted: 'تم رفع إيصال الدفع', payment_confirmed: 'تم تأكيد الدفع',
  payment_rejected: 'تم رفض الدفع', trip_assigned: 'تم تعيين الرحلة', received_by_office: 'تم الاستلام بالمكتب',
  verified: 'تم التحقق من الطرد', loaded: 'تم التحميل على الباص', in_transit: 'الطرد في الطريق',
  arrived: 'وصل الطرد إلى الوجهة', ready_for_collection: 'الطرد جاهز للاستلام', collected: 'تم تسليم الطرد',
  returned: 'تم إرجاع الطرد', cancelled: 'تم إلغاء الطرد', rejected: 'تم رفض الطرد',
  rejected_at_dropoff: 'تعذّر قبول الطرد بالمكتب', damaged: 'تعرض الطرد لتلف', lost: 'الطرد مفقود — جارٍ البحث عنه',
  dropoff_deadline_expired: 'انتهى موعد تسليم الطرد قبل الرحلة', late_exception_approved: 'تم اعتماد تجاوز استثنائي لموعد التسليم',
};

// Anything the customer would reasonably want a human to explain — surfaces
// a WhatsApp prompt instead of leaving them staring at a status label.
// 'returned' alone is ambiguous (also the normal exit for "never collected"),
// so that one is keyed off the specific event instead of the coarse status.
const PROBLEM_STATUSES = new Set(['rejected', 'damaged', 'lost', 'dropoff_deadline_expired']);
const PROBLEM_EVENTS    = new Set(['rejected_at_dropoff', 'payment_rejected', 'damaged', 'lost']);
const SUPPORT_WHATSAPP = WHATSAPP_NUMBER;

const TrackParcelPage = () => {
  const { trackingNumber } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const timeline = data?.timeline || [];
  const latestEvent = timeline[timeline.length - 1];
  const hasProblem = !!data && (
    PROBLEM_STATUSES.has(data.status) || (latestEvent && PROBLEM_EVENTS.has(latestEvent.event))
  );

  useEffect(() => {
    parcelAPI.track(trackingNumber)
      .then((res) => setData(res.data.data))
      .catch(() => setError('رقم التتبع غير موجود'))
      .finally(() => setLoading(false));
  }, [trackingNumber]);

  return (
    <div dir="rtl" className="min-h-screen bg-[#F5F9FF] flex items-start justify-center py-10 px-4">
      <div className="w-full max-w-lg">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 mb-6 text-[#1E3A8A] font-bold text-sm">
          <img src="/logo.png" alt="سافر" className="w-8 h-8 rounded-lg" /> سافر
        </button>

        {loading ? (
          <div className="bg-white rounded-2xl border p-10 text-center text-gray-400">جاري التحميل...</div>
        ) : error ? (
          <div className="bg-white rounded-2xl border p-10 text-center">
            <div className="text-5xl mb-3">❌</div>
            <p className="text-gray-700 font-bold">{error}</p>
          </div>
        ) : data && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <p className="text-xs text-gray-500 mb-1">رقم التتبع</p>
            <p className="font-mono font-black text-2xl text-blue-700 mb-4">{data.trackingNumber}</p>

            <span className={`inline-block px-3 py-1.5 rounded-full text-sm font-bold border mb-5 ${
              PROBLEM_STATUSES.has(data.status) ? 'bg-red-50 text-red-700 border-red-200' : 'bg-blue-50 text-blue-700 border-blue-100'
            }`}>
              {STATUS_LABELS[data.status] || data.status}
            </span>

            {hasProblem && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-5">
                <p className="text-sm font-bold text-red-800 mb-1">⚠️ يوجد أمر يحتاج متابعة مع خدمة العملاء</p>
                <p className="text-xs text-red-700 mb-3">تواصل معنا عبر واتساب وسنساعدك في أقرب وقت.</p>
                <a
                  href={`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(`مرحباً، لدي استفسار بخصوص الطرد رقم ${data.trackingNumber}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12.017 2.003c-5.514 0-9.997 4.483-9.997 9.997 0 1.764.464 3.489 1.346 5.007L2.03 22l5.146-1.35a9.958 9.958 0 004.841 1.234h.004c5.514 0 9.997-4.483 9.997-9.997 0-2.67-1.04-5.182-2.929-7.071a9.935 9.935 0 00-7.072-2.929l.001.003z"/></svg>
                  تواصل عبر واتساب
                </a>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 text-sm mb-5">
              <div><p className="text-xs text-gray-500">المرسل</p><p className="font-semibold text-gray-800">{data.senderName}</p></div>
              <div><p className="text-xs text-gray-500">المستلم</p><p className="font-semibold text-gray-800">{data.receiverName}</p></div>
              {data.departureOffice && <div><p className="text-xs text-gray-500">مكتب المغادرة</p><p className="font-semibold text-gray-800">{data.departureOffice.name} — {data.departureOffice.city}</p></div>}
              {data.destinationOffice && <div><p className="text-xs text-gray-500">مكتب الوجهة</p><p className="font-semibold text-gray-800">{data.destinationOffice.name} — {data.destinationOffice.city}</p></div>}
            </div>

            {data.trip && (
              <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-5 text-sm">
                <p className="font-bold text-blue-800">🚌 {data.trip.company?.name}</p>
                <p className="text-gray-700 mt-1">{data.trip.fromCity} ← {data.trip.toCity}</p>
                <p className="text-xs text-gray-500 mt-1">
                  الانطلاق: {new Date(data.trip.departureTime).toLocaleString('ar-SA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  {data.trip.arrivalTime && <> — الوصول المتوقع: {new Date(data.trip.arrivalTime).toLocaleString('ar-SA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</>}
                </p>
              </div>
            )}

            <p className="text-xs font-bold text-gray-600 mb-2">سجل الرحلة</p>
            <div className="space-y-3">
              {timeline.map((ev, i) => (
                <div key={ev.id || i} className="flex items-start gap-3">
                  <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${PROBLEM_EVENTS.has(ev.event) ? 'bg-red-500' : 'bg-blue-500'}`} />
                  <div>
                    {/* Falls back through STATUS_LABELS before the raw code —
                        most event names already match a status name 1:1, so
                        this alone covers most gaps in EVENT_LABELS. */}
                    <p className="text-sm font-semibold text-gray-800">{EVENT_LABELS[ev.event] || STATUS_LABELS[ev.event] || ev.event}</p>
                    <p className="text-xs text-gray-400">{new Date(ev.createdAt).toLocaleString('ar-SA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackParcelPage;
